import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { Telescope } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);

  const from = location.state?.from?.pathname || "/";

  const handleSuccess = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      toast.error("Google returned no credential — try again");
      return;
    }
    setBusy(true);
    try {
      await login(credential);
      navigate(from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error(`Auth rejected: ${detail || "invalid token"}`);
      } else if (status >= 500) {
        toast.error(`Server error (${status})${detail ? ": " + detail : ""}`);
      } else if (err?.message?.toLowerCase().includes("network")) {
        toast.error("Cannot reach the API — check CORS or backend status");
      } else {
        toast.error(detail || err?.message || "Sign-in failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div
        className="w-full max-w-md bg-white rounded-3xl border border-stone-200 shadow-sm p-10 text-center"
        data-testid={LOGIN.submitButton}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#4A5D4E] flex items-center justify-center text-[#F9F8F6] -rotate-6">
            <Telescope size={26} strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-medium tracking-tight">
              LeaseDeck
            </h1>
            <p className="overline mt-1">Your Rental Shortlist</p>
          </div>
        </div>

        <p className="text-stone-500 mt-6 mb-8 leading-relaxed">
          Sign in to organize, compare, and shortlist the rentals you love.
        </p>

        <div
          className="flex justify-center"
          data-testid={LOGIN.googleButton}
          aria-busy={busy}
        >
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => toast.error("Google sign-in failed")}
            shape="pill"
            size="large"
            text="continue_with"
          />
        </div>

        <p className="text-xs text-stone-400 mt-8">
          We only use your Google account to identify you. Nothing is posted on
          your behalf.
        </p>
      </div>
    </div>
  );
}
